import StencilHTMLElement from "./stencil-element";

class ProductDetails extends StencilHTMLElement {
  name = "ProductDetails";
  form;
  product;
  productOptionsPicker;
  sellingPlanPicker;
  quantityPicker;

  swiperMedia;
  swiperThumbs;

  busy = false;

  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector('.product-form');
    this.form.querySelector('[name=id]').disabled = false;

    this.form.addEventListener('submit', this.onSubmit.bind(this));

    this.productOptionsPicker = this.form.querySelector('product-options-picker');
    this.sellingPlanPicker = this.querySelector("#selling-plan-select");

    this.product = JSON.parse(this.querySelector("[data-product-json]").innerHTML);

    document.addEventListener('variant:changed', this.onVariantChanged.bind(this));

    this.quantityPicker = this.querySelector("[name='quantity']");
    if(this.quantityPicker){
      this.quantityPicker.addEventListener("change", e => {
        let event = new CustomEvent("quantity:changed", { detail: {
          variant: this.productOptionsPicker.currentVariant,
          quantity: e.currentTarget.value
        }});
        this.dispatchEvent(event);
      });
    }

    customElements.whenDefined("swiper-slider").then(() => {
      this.swiperMedia = this.querySelector('swiper-slider[id$="full"]')?.instance;
      this.swiperThumbs = this.querySelector('swiper-slider[id$="thumbs"]')?.instance;
    });

    // Handle sticky add button
    if(this.querySelector("#sticky-add")){
      this.querySelector("#sticky-add").addEventListener("click", () => this.form.dispatchEvent(new CustomEvent('submit', {cancelable: true})));
    }
  }

  async onSubmit(e) {
    e.preventDefault();

    if (this.hasAttribute("data-purchase-gate")) {
      const actions = this.querySelector("[data-purchase-actions]");
      if (actions?.classList.contains("d-none")) return;
    }

    if(this.busy) return;
    this.busy = true;

    // Update visual state as we process the submission. There might be multiple
    // add buttons on the page - this disables them all.
    this.querySelectorAll('[data-disable-on-add]').forEach(element => {
      element.classList.add('loading');
      element.setAttribute('aria-disabled', "true");
    });
    this.querySelectorAll('.spinner-overlay').forEach(element => element.classList.remove('d-none'));

    // Construct the submission body - this uses FormData, which will get every
    // form field in the form (including 'properties')
    const formData = new FormData(this.form);
    formData.append('sections_url', window.location.pathname);

    // Remove any form inputs with an empty string value
    let emptyKeys = [];
    for (const pair of formData.entries()) {
      if(pair[1] === ""){
        emptyKeys.push(pair[0]);
      }
    }
    emptyKeys.forEach(emptyKey => {
      formData.delete(emptyKey);
    });

    // Certain apps (e.g. GiftShip) expect the product form to handle adding
    // their additional products. However, this won't work with FormData when
    // the 'primary' product has a selling plan, as the additional products
    // won't have a selling plan, and add.js will try and use the same selling
    // plan ID for all IDs. So in those cases, we need to strip it out into a 
    // standard variant items array for cart.js
    let variantIds = [];
    let sellingPlan = false;
    let properties = {};
    for (const pair of formData.entries()) {
      if(pair[0] == "id[]") {
        variantIds.push(pair[1]);
      }
      if(pair[0] == "selling_plan") {
        sellingPlan = pair[1];
      }
      if(pair[0].startsWith("properties")) {
        properties[pair[0].replace( /(^.*\[|\].*$)/g, '' )] = pair[1];
      }
    }

    // Check if we've found more than 1 variant ID, AND a selling plan. If so,
    // we need to create a standard items array of variants to add.
    let items = false;
    if(variantIds.length > 1 && sellingPlan){
      let primaryVariantId = variantIds.shift();
      items = [{"id": primaryVariantId, "quantity": formData.get("quantity"), "selling_plan": sellingPlan, "properties": properties }];
      variantIds.forEach(id => {
        items.push({"id": id, "quantity": formData.get("quantity")});
      });
    }

    // Determine whether to send items, or formData
    let dataToSend = items ? items : formData;

    console.log(this.logPrefix, "Data to send", dataToSend);

    // Add the submitted product to the cart
    await window.Stencil.Extensions.Cart.add(dataToSend).finally(e => {
      this.querySelectorAll('[data-disable-on-add]').forEach(element => {
        element.classList.remove('loading');
        element.removeAttribute('aria-disabled');
      });
      
      this.querySelectorAll('.spinner-overlay').forEach(element => element.classList.add('d-none'));
    });

    this.busy = false;
  }

  onVariantChanged(e) {
    const variant = e.detail.variant;
    const target = e.detail.target;
    if(target !== this) return;
    // Update Swiper current slide
    const matchingSlide = this.querySelector(`[data-variants*='${variant.id}']:not(.swiper-slide-duplicate)`);
    if (matchingSlide) {
      const slideIndex = matchingSlide.dataset.index;
      if (slideIndex) {
        this.swiperMedia?.slideToLoop(parseInt(slideIndex));
      }
    }

    // Update Klarna
    window.Klarna?.OnsiteMessaging.refresh();

    // Update pickup availability
    this.refreshPickupAvailability(variant.id);
  }

  refreshPickupAvailability(variantId) {
    fetch(window.Shopify.routes.root + "variants/" + variantId + "/?section_id=pickup-availability")
      .then(response => response.text())
      .then(text => {
        const container = document.querySelector('[data-store-availability-container]');
        if(container) {
          const pickupAvailabilityHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.pickup-availability-preview');

          container.innerHTML = '';
          if(pickupAvailabilityHTML){
            container.appendChild(pickupAvailabilityHTML);
          }
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}

export default ProductDetails;